/**
 * Kanonische Form-Feld-Stile (SSOT) — vereinheitlicht Label-, Input- und
 * Hint-Styling über Formulare hinweg. Analog zu `page-heading.ts` (PAGE_H1_CLASS):
 * ein Ort, kein per-Form-Drift.
 *
 * Hintergrund (Visual-Critique 2026-06-22, /settings/profile): ProfilePage und
 * ChangePasswordForm hatten semantisch identische Felder völlig unterschiedlich
 * gestylt — mono-uppercase vs. sentence-case Labels, `rounded-md` vs `rounded-lg`
 * Inputs, `focus:ring-brand-green-700` vs `focus-visible:ring-brand-green-500`,
 * und ein Hint in `text-brand-green-400` (#6eab8a ≈ 2.6:1 auf Weiß → WCAG-AA
 * verfehlt). Diese SSOT löst die Drift auf der Profil-Seite auf.
 *
 * Kanon-Entscheidungen:
 *   - Label: mono-uppercase ist die App-Mehrheit (ProfilePage, CollectionList,
 *     RenamePatternFields) → kanonisch.
 *   - Input-Radius: `rounded-md` (= Card-Radius + Button-Radius).
 *   - Focus: `focus-visible:` (keyboard-only, kein Maus-Klick-Ring) + Ring-Token
 *     `brand-green-500` (= Button-Primitive).
 *   - Hint: `text-brand-green-600` (#2d6b4f ≈ 6.3:1 auf Weiß → AA ✓). NICHT
 *     green-400/500 (beide < 4.5:1 bei <18px).
 *
 * Anwendung: `className={FIELD_LABEL_CLASS}` etc. Input-Größe getrennt von der
 * Chrome: `FIELD_INPUT_CLASS` (schlank) vs `FIELD_INPUT_COMPACT_CLASS` (dichte
 * Modals) teilen `FIELD_INPUT_CHROME`. Migriert: ProfilePage, ChangePasswordForm,
 * InviteUserModal, BrandEditModal, BrandUploadModal. Bewusst gelassen (eigene
 * Control-Familie, kompakt): FilterCombobox, ProductCategoryTree.
 */

// Redesign S51: ruhigeres Label — kein mono, engeres Tracking (0.02em statt wider),
// neutrale Farbe (ink-500). Uppercase bleibt (Section-Label-Konvention), aber
// weniger geschrien.
export const FIELD_LABEL_CLASS =
  'block text-xs2 font-semibold tracking-[0.02em] text-ink-500 uppercase mb-1.5';

/**
 * Reine visuelle Chrome eines Eingabefelds — Border, Radius, Fokus-Ring,
 * Disabled-Look. Größen-/Layout-AGNOSTISCH (kein `w-full`, kein Padding, keine
 * Schriftgröße), damit schlanke Formulare UND dichte Modals dieselbe Optik +
 * Fokus-a11y teilen, aber ihre eigene Dichte behalten (SSOT-Split 2026-06-22:
 * Brand-Modals brauchten den Fokus-Ring, nicht das großzügige Profil-Padding).
 */
// Redesign S51: weicher Focus-Glow (ring-4 @ 20% statt harter 2px-Ring) + grüner
// Border im Fokus + subtiler Hover-Border. `transition` animiert den Glow.
export const FIELD_INPUT_CHROME =
  'border border-ink-200 rounded-md transition hover:border-ink-300 ' +
  'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-green-500/20 focus-visible:border-brand-green-600 ' +
  'disabled:bg-ink-50 disabled:text-brand-green-600';

/** Eingabefeld für schlanke Formulare (Profil, ChangePassword, Invite):
 * volle Breite, großzügiges Padding (py-3), Basis-Schriftgröße + Chrome.
 * Byte-identisch zur vorherigen Definition (keine Verhaltensänderung). */
export const FIELD_INPUT_CLASS = `w-full px-4 py-3 ${FIELD_INPUT_CHROME}`;

/** Kompaktes Eingabefeld für dichte Modals (Brand-Edit/-Upload, 9–10 Felder):
 * kleineres Padding (py-2) + text-sm, gleiche Chrome inkl. Fokus-Ring. */
export const FIELD_INPUT_COMPACT_CLASS = `w-full px-3 py-2 text-sm ${FIELD_INPUT_CHROME}`;

/** Helfer-Text unter einem Feld. green-600 = AA-konform (≠ green-400). */
export const FIELD_HINT_CLASS = 'mt-1 text-xs2 text-ink-500';
